import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import {
  CustomModels,
  Model
} from '@utils/api/custom-models/use-get-custom-models.tsx';
import {
  getContributionTarget,
  setContributionTarget
} from '@utils/localStorage/contribution-target.ts';
import { isMultilingualLanguage } from '@utils/editors/training-clip-windows.ts';

// ACTIVE_TRAINING_STATUSES on the backend: READY_TO_RUN=2, RUNNING=3.
// NOT_RUNNING=1 (default for new models) is fine for contributions.
const ACTIVE_TRAINING = [2, 3];

// Case-insensitive; treat regional variants as the same language (session
// "en-US" should match model "en" and vice versa) so the list does not hide
// models on cosmetic locale differences. The API still checks on submit.
const sameLanguage = (a?: string | null, b?: string | null) => {
  if (!a || !b) return false;
  const norm = (s: string) => s.toLowerCase().split(/[-_]/)[0];
  return norm(a) === norm(b);
};

/**
 * The models a session can contribute to, and which one is picked.
 *
 * Shared by the caption and transcript editors so both offer the same models
 * and remember the same choice (the model page opened last, see
 * ``contribution-target``).
 */
export const useContributionModels = (languageKey?: string | null) => {
  const { data, isLoading } = useQuery<CustomModels>(
    ['orgCustomModels'],
    async () => {
      const { data: models } = await Axios.get<CustomModels>(
        GET_URL('0', '100').customModelsList
      );
      return models;
    },
    { staleTime: 60_000 }
  );

  const isMultilingual = isMultilingualLanguage(languageKey);

  const inLanguage = useMemo<Model[]>(() => {
    const all = data?.result ?? [];
    // A multilingual session records no language, so no model can match it:
    // offer every model and let the customer pick the language spoken.
    if (!languageKey || isMultilingual) return all;
    return all.filter(m => {
      if (sameLanguage(m.base_language_key, languageKey)) return true;
      if (m.base_language_key == null) {
        return sameLanguage(m.key?.split('-')[1], languageKey);
      }
      return false;
    });
  }, [data, languageKey, isMultilingual]);

  const eligibleModels = useMemo<Model[]>(
    () =>
      inLanguage.filter(
        m => !ACTIVE_TRAINING.includes(m.training_log?.training_status ?? 0)
      ),
    [inLanguage]
  );

  const [selectedModelId, setSelectedModelId] = useState<number | null>(
    getContributionTarget
  );

  useEffect(() => {
    if (eligibleModels.length === 0) return;
    if (!eligibleModels.some(m => m.id === selectedModelId)) {
      // Not persisted: the model page the customer opened stays the target
      // for next time, even while it is mid-training and cannot take clips.
      setSelectedModelId(eligibleModels[0].id);
    }
  }, [eligibleModels, selectedModelId]);

  const selectModel = (id: number) => {
    setSelectedModelId(id);
    setContributionTarget(id);
  };

  return {
    isLoading,
    isMultilingual,
    eligibleModels,
    allMidTraining: inLanguage.length > 0 && eligibleModels.length === 0,
    selectedModelId,
    selectedModelName: eligibleModels.find(m => m.id === selectedModelId)?.name,
    selectModel
  };
};
